import { Subject } from 'rxjs';
import Log from '../../util/log';

export enum MessageType {
  'connect',
  'disconnect',
  'message',
  'error',
}

export type PlatformMessage = {
  type: MessageType;
  data?: Record<string, unknown> | undefined;
};

export type PlatformSocketType = {
  isOpen: boolean;
  connect: () => Promise<PlatformSocketType>;
  events: Subject<PlatformMessage>;
  close: () => void;
  sendMessage: (message: Record<string, any>) => void;
};

export default class PlatformSocket implements PlatformSocketType {
  private shouldRetry = false;

  private delayTime = 1000;

  private address: string;

  private appId: string;

  private getAccessToken: () => Promise<string>;

  private socket: WebSocket | undefined;

  private retryTimer: number | undefined | any;

  private subject = new Subject<PlatformMessage>();

  private messageId = 0;

  private messageQueue: Record<string, any>[] = [];

  private connecting: Promise<PlatformSocket> | undefined;

  private pingDelay = 60000;

  private pingTimeoutId: any;

  constructor(appId: string, address: string, getAccessToken: () => Promise<string>) {
    this.address = address;
    this.appId = appId;
    this.getAccessToken = getAccessToken;
  }

  /** This will be true if the connection is ready to send and receive messages */
  get isOpen(): boolean {
    return !!(this.socket && this.socket.readyState === 1);
  }

  /**
   * The connect function establishes a connection to the WebSocket.
   * @public
   * @return {Promise<WebSockets>}
   */
  async connect(): Promise<PlatformSocket> {
    if (this.connecting) {
      return this.connecting;
    }
    // if the websocket is connected or connecting already, then stop
    if (this.socket) {
      return this;
    }

    // Stay connected after this point
    this.shouldRetry = true;
    this.connecting = this.getAccessToken()
      .then((token) => {
        const url = `${this.address}?app_id=${encodeURIComponent(
          this.appId,
        )}&token=${encodeURIComponent(token)}`;
        this.socket = new WebSocket(url);
        this.socket.addEventListener('open', this.handleConnected);
        this.socket.addEventListener('message', this.handleMessage);
        this.socket.addEventListener('error', this.handleError);
        this.socket.addEventListener('close', this.handleClose);

        this.sendNextCommand();
        this.connecting = undefined;
        document.addEventListener('visibilitychange', this.onDocumentVisibilityChange);
        this.resetPing();
        return this;
      })
      .catch((error) => {
        Log.warn(error);
        this.connecting = undefined;
        this.socket = undefined;
        this.retryConnection();
        return this;
      });

    // Done
    return this.connecting;
  }

  get events(): Subject<PlatformMessage> {
    return this.subject;
  }

  handleMessage = (event: { data: string }): void => {
    const data = JSON.parse(event.data);
    this.subject.next({ type: MessageType.message, data });
    this.resetPing();
  };

  handleConnected = (): void => {
    this.delayTime = 1000;
    this.subject.next({ type: MessageType.connect });
  };

  /**
   * When the connection drops or the Websocket is closed.
   * This function will auto-retry connection until successfully connected
   * @private
   * @return {Promise<WebSockets>} returns the connection function
   */
  retryConnection(): void {
    // Clear previous retry timer
    if (this.retryTimer) {
      clearTimeout(this.retryTimer);
    }

    // Create a new retry timer
    this.retryTimer = setTimeout(() => {
      // Clear timer
      this.retryTimer = undefined;

      // Check if we want to be connected
      if (!this.shouldRetry) {
        return;
      }

      // Increase retry delay for next time
      if (this.delayTime < 4000) {
        this.delayTime += 1000;
      }

      // connect again
      this.connect();
    }, this.delayTime);
  }

  /**
   * Handles the Web socket error event. We don't need to retry, because handleClose is also called on errors.
   * @private
   * @param {Error} err The error that happened
   */
  handleError = (error): void => {
    this.socket = undefined;
    const { message } = error as { message: string };
    Log.warn(`[WebSocket] Connection failed: ${message}`);
    this.subject.next({ type: MessageType.error, data: { message } });
  };

  /**
   * Handles the Web socket close event
   * @private
   * @param  {Event} e no need for inputting, It is a Websocket Event
   */
  handleClose = (): void => {
    Log.warn('[WebSocket] closed');
    this.socket = undefined;
    this.retryConnection();
  };

  /**
   * @public
   * Forcefully closes the Web socket.
     Note: Socket will be set to null. Auto connect will be disabled.
   */
  close(): void {
    // Prevent retrying
    this.shouldRetry = false;

    // Cancel retry timer if there is one
    if (this.retryTimer) {
      clearTimeout(this.retryTimer);
      this.retryTimer = undefined;
    }

    // Close socket if it's open
    if (this.socket) {
      this.socket.close();
      this.socket.removeEventListener('open', this.handleConnected);
      this.socket.removeEventListener('message', this.handleMessage);
      this.socket.removeEventListener('error', this.handleError);
      this.socket.removeEventListener('close', this.handleClose);
      this.socket = undefined;
      this.subject.next({ type: MessageType.disconnect });
    }

    document.removeEventListener('visibilitychange', this.onDocumentVisibilityChange);
  }

  sendMessage(message: Record<string, any>): void {
    const payload = { ...message };
    payload.id = `${this.messageId}`;
    this.messageId += 1;
    this.messageQueue.push(message);
    this.sendNextCommand();
  }

  sendNextCommand(): void {
    if (this.socket && this.socket.readyState === 1) {
      const cmd = this.messageQueue.shift();
      if (cmd) {
        this.socket.send(JSON.stringify(cmd));
        this.sendNextCommand();
        Log.info(`Sending WS command: ${JSON.stringify(cmd)}`);
      }
    } else {
      Log.warn('WebSocket: Attempted to send message up, but the socket is not ready.');
    }
  }

  ping(): void {
    if (this.socket) {
      this.sendMessage({ id: '1', version: '1', type: 'command', cmd: 'ping' });
      this.resetPing();
    }
  }

  resetPing(): void {
    if (this.pingTimeoutId) {
      clearTimeout(this.pingTimeoutId);
    }
    this.pingTimeoutId = setTimeout(() => {
      this.ping();
    }, this.pingDelay);
  }

  onDocumentVisibilityChange = () => {
    if (document.visibilityState === 'visible') {
      // check if websocket is still active after resuming
      this.ping();
    }
  };
}
